import React, { useEffect, useRef, useState } from 'react';
import { Plus, Save, Trash2, Flame, Leaf, Loader2, Upload, ImageIcon, Percent, Sparkles } from 'lucide-react';
import { vendorAdminApi } from '../services/api';
import api from '../services/api';
import { useVendorContext } from './VendorAdminLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import VendorGalleryPanel from './VendorGalleryPanel';

const VendorMenuPage = () => {
  const { vendor_id, impersonating } = useVendorContext();
  const [sections, setSections] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeVid, setActiveVid] = useState(null);
  const [uploading, setUploading] = useState(null); // "sIdx-iIdx" while uploading
  const [aiDescribing, setAiDescribing] = useState(null); // item.id while running
  const fileInputRef = useRef(null);
  const uploadTargetRef = useRef({ sIdx: 0, iIdx: 0 });

  useEffect(() => {
    (async () => {
      try {
        const m = await vendorAdminApi.menu(impersonating ? vendor_id : undefined);
        setSections(m.sections || []);
        setActiveVid(m.vendor_id);
      } catch { toast.error('Failed to load menu'); }
    })();
  }, [vendor_id, impersonating]);

  const triggerUpload = (sIdx, iIdx) => {
    uploadTargetRef.current = { sIdx, iIdx };
    fileInputRef.current?.click();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/^image\//.test(file.type)) { toast.error('Please pick an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image is larger than 5 MB'); return; }
    const { sIdx, iIdx } = uploadTargetRef.current;
    const key = `${sIdx}-${iIdx}`;
    setUploading(key);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/files', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      updateItem(sIdx, iIdx, 'image', data.url);
      toast.success('Photo uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(null);
    }
  };

  const addSection = () => setSections((p) => [...p, { title: 'New section', items: [] }]);
  const removeSection = (idx) => setSections((p) => p.filter((_, i) => i !== idx));
  const updateSection = (idx, key, value) => setSections((p) => p.map((s, i) => (i === idx ? { ...s, [key]: value } : s)));
  const addItem = (sIdx) => {
    const id = `${activeVid || 'item'}-${Date.now()}`;
    updateSection(sIdx, 'items', [...sections[sIdx].items, { id, name: 'New item', description: '', price: 0, image: '', popular: false, veg: false }]);
  };
  const removeItem = (sIdx, iIdx) => updateSection(sIdx, 'items', sections[sIdx].items.filter((_, i) => i !== iIdx));
  const updateItem = (sIdx, iIdx, key, value) =>
    updateSection(sIdx, 'items', sections[sIdx].items.map((it, i) => (i === iIdx ? { ...it, [key]: value } : it)));

  const aiDescribe = async (sIdx, iIdx) => {
    const it = sections[sIdx].items[iIdx];
    if (!it?.id) { toast.error('Save the menu once so the item has an ID, then try AI again.'); return; }
    if (!it.name?.trim()) { toast.error('Add a name first — AI needs something to describe.'); return; }
    setAiDescribing(it.id);
    try {
      const { data } = await api.post(`/vendor-admin/menu/items/${it.id}/ai-describe`,
        {}, { params: impersonating ? { vendor_id } : undefined });
      if (data?.description) {
        updateItem(sIdx, iIdx, 'description', data.description);
        toast.success('AI description written');
      }
    } catch (err) { toast.error(err?.response?.data?.detail || 'AI description failed'); }
    finally { setAiDescribing(null); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = sections.map((s) => ({
        title: s.title,
        items: s.items.map((it) => ({
          id: it.id || `${activeVid}-${Math.random().toString(36).slice(2, 8)}`,
          name: it.name,
          description: it.description || '',
          price: Number(it.price) || 0,
          image: it.image || '',
          popular: !!it.popular,
          veg: !!it.veg,
          // Tax-exempt is stored as an explicit tri-state on the item — true
          // forces tax off, false forces tax on, undefined inherits from the
          // vendor category default (see marketplace_service.item_is_taxable).
          ...(typeof it.tax_exempt === 'boolean' ? { tax_exempt: it.tax_exempt } : {}),
        })),
      }));
      await vendorAdminApi.putMenu(cleaned, impersonating ? vendor_id : undefined);
      toast.success('Menu saved');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  if (sections === null) return <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;

  return (
    <div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFile}
        className="hidden"
        data-testid="menu-image-file-input"
      />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Menu</h1>
          <p className="text-slate-500 mt-1">Manage products & services offered by your store.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={addSection}><Plus className="h-4 w-4 mr-1.5" />Add section</Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white"><Save className="h-4 w-4 mr-1.5" />{saving ? 'Saving…' : 'Save menu'}</Button>
        </div>
      </div>

      <div className="mt-6 space-y-6">
        <VendorGalleryPanel vendorId={vendor_id} impersonating={impersonating} />
        {sections.length === 0 && (
          <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-slate-500">
            No sections yet. <button className="text-emerald-700 underline" onClick={addSection}>Add first section</button>.
          </div>
        )}
        {sections.map((s, sIdx) => (
          <section key={s.id || `${s.title || 'section'}-${sIdx}`} className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className="flex items-center gap-3">
              <Input value={s.title} onChange={(e) => updateSection(sIdx, 'title', e.target.value)} className="max-w-xs h-10 font-semibold" />
              <span className="text-xs text-slate-500">{s.items.length} items</span>
              <div className="flex-1" />
              <Button size="sm" variant="outline" onClick={() => addItem(sIdx)}><Plus className="h-3.5 w-3.5 mr-1" />Add item</Button>
              <Button size="sm" variant="outline" onClick={() => removeSection(sIdx)} className="text-rose-600 hover:text-rose-700 border-rose-200"><Trash2 className="h-3.5 w-3.5 mr-1" />Remove</Button>
            </div>
            <div className="mt-4 space-y-3">
              {s.items.length === 0 && <div className="text-sm text-slate-500 text-center py-3">No items.</div>}
              {s.items.map((it, iIdx) => (
                <div key={it.id || iIdx} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-start bg-slate-50 rounded-xl p-3">
                  <div className="md:col-span-3"><Label className="text-xs text-slate-600">Name</Label><Input value={it.name} onChange={(e) => updateItem(sIdx, iIdx, 'name', e.target.value)} className="h-9 mt-1" /></div>
                  <div className="md:col-span-4">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs text-slate-600">Description</Label>
                      <button
                        type="button"
                        onClick={() => aiDescribe(sIdx, iIdx)}
                        disabled={aiDescribing === it.id}
                        className="text-[11px] font-semibold text-indigo-700 hover:text-indigo-800 disabled:opacity-50 inline-flex items-center gap-1"
                        title="Draft this description with AI"
                        data-testid={`menu-ai-describe-${it.id || `${sIdx}-${iIdx}`}`}
                      >
                        {aiDescribing === it.id
                          ? <><Loader2 className="h-3 w-3 animate-spin" />Writing…</>
                          : <><Sparkles className="h-3 w-3" />Write with AI</>}
                      </button>
                    </div>
                    <Input value={it.description} onChange={(e) => updateItem(sIdx, iIdx, 'description', e.target.value)} className="h-9 mt-1" />
                  </div>
                  <div className="md:col-span-1"><Label className="text-xs text-slate-600">$</Label><Input type="number" step="0.01" value={it.price} onChange={(e) => updateItem(sIdx, iIdx, 'price', e.target.value)} className="h-9 mt-1" /></div>
                  <div className="md:col-span-3">
                    <Label className="text-xs text-slate-600">Photo</Label>
                    <div className="mt-1 flex items-center gap-2">
                      {it.image ? (
                        <img
                          src={it.image}
                          alt={it.name || 'item'}
                          className="h-9 w-9 rounded object-cover border border-slate-200 flex-shrink-0"
                          data-testid={`menu-item-thumb-${sIdx}-${iIdx}`}
                        />
                      ) : (
                        <div
                          className="h-9 w-9 rounded bg-slate-200 flex items-center justify-center flex-shrink-0"
                          aria-label="No photo"
                        >
                          <ImageIcon className="h-4 w-4 text-slate-400" />
                        </div>
                      )}
                      <Input
                        value={it.image}
                        onChange={(e) => updateItem(sIdx, iIdx, 'image', e.target.value)}
                        className="h-9 flex-1"
                        placeholder="Image URL or upload →"
                      />
                      <button
                        type="button"
                        onClick={() => triggerUpload(sIdx, iIdx)}
                        disabled={uploading === `${sIdx}-${iIdx}`}
                        className="h-9 px-2 text-xs bg-slate-900 text-white rounded hover:bg-slate-800 disabled:opacity-50 inline-flex items-center gap-1 flex-shrink-0"
                        data-testid={`menu-item-upload-${sIdx}-${iIdx}`}
                        title="Upload a photo (max 5 MB)"
                      >
                        {uploading === `${sIdx}-${iIdx}`
                          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          : <Upload className="h-3.5 w-3.5" />}
                      </button>
                    </div>
                  </div>
                  <div className="md:col-span-1 flex flex-col gap-1 pt-5">
                    <label className="flex items-center gap-1 text-xs text-slate-700" title="Mark as customer favourite">
                      <input type="checkbox" className="accent-orange-500" checked={!!it.popular} onChange={(e) => updateItem(sIdx, iIdx, 'popular', e.target.checked)} />
                      <Flame className="h-3 w-3 text-orange-500" />
                    </label>
                    <label className="flex items-center gap-1 text-xs text-slate-700" title="Vegetarian">
                      <input type="checkbox" className="accent-emerald-600" checked={!!it.veg} onChange={(e) => updateItem(sIdx, iIdx, 'veg', e.target.checked)} />
                      <Leaf className="h-3 w-3 text-emerald-600" />
                    </label>
                    <label
                      className="flex items-center gap-1 text-xs text-slate-700"
                      title="Taxable — when off, sales tax is not charged on this item"
                      data-testid={`menu-item-taxable-label-${sIdx}-${iIdx}`}
                    >
                      <input
                        type="checkbox"
                        className="accent-indigo-600"
                        checked={it.tax_exempt !== true}
                        onChange={(e) => updateItem(sIdx, iIdx, 'tax_exempt', !e.target.checked)}
                        data-testid={`menu-item-taxable-${sIdx}-${iIdx}`}
                      />
                      <Percent className="h-3 w-3 text-indigo-600" />
                    </label>
                  </div>
                  <div className="md:col-span-12 flex justify-end">
                    <Button size="sm" variant="ghost" onClick={() => removeItem(sIdx, iIdx)} className="text-rose-600 hover:text-rose-700"><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};

export default VendorMenuPage;
